import { Component } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators, ValidationErrors } from '@angular/forms';

import { BonesErrorService, BonesError } from '@bones/core';
import { BonesForm } from '../../class/BonesForm';
// import { BonesFormItemPickerValues } from '@bones/form/class/BonesFormItemPickerValues';
import { BonesFormItemPickerValues } from '../../class/BonesFormItemPickerValues';

/**
 * This test tool uses the various form controls.
 * 
 * It creates its own FormGroup and edit form for more control over the form that what is offered
 * by BonesFormEdit.
 */
@Component({
  templateUrl: 'test-tools-bones-form-controls.html'
})
export class TestToolsBonesFormControlsPage
{
    display: any;
    bf: BonesForm;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private bes: BonesErrorService,
    )
    {
        // Create map with numbers as keys
        const picker3Map = new Map<number, string>();
        picker3Map.set(1, 'One');
        picker3Map.set(2, 'Two');
        picker3Map.set(3, 'Three');
        picker3Map.set(4, 'Four');

        // Create map with strings as keys
        const picker4Map = new Map<string, string>();
        picker4Map.set('A', 'Apple');
        picker4Map.set('B', 'Banana');
        picker4Map.set('C', 'Cherry');

        // Create form and items
        this.bf = new BonesForm(
        {
            formBuilder: this.formBuilder,
            columns:
            [
                {
                    name: 'field1',
                    validator: Validators.required,
                    // hidden on bones form because it is manually placed in html
                    hidden: true,
                    onChange: (v, i) => console.log('field1 change', v, i)
                },
                {
                    name: 'miniTest',
                    title: 'Test minimum and maximum length',
                    validator: [ Validators.required, Validators.minLength(12), Validators.maxLength(15) ],
                    errorMessages:
                    {
                        // commented out in order to use default minlength message
                        // minlength: 'Must enter at least 12 characters.',
                        // override default maxlength message
                        maxlength: 'Enter between 12 to 15 characters.',
                    },
                    help: 'This field requires a minimum of 12 characters and must not have more than 15 characters.'
                        + ' These are silly rules, but, hey, who knows what requirement may come along?'
                },
                {
                    name: 'rofield',
                    title: 'Readonly Field',
                    initialValue: 'You can\'t change me',
                    readonly: true,
                    help: 'Ha! ha!'
                },
                {
                    name: 'dog',
                    title: 'Who\'s a good dog?',
                    validator: TestToolsBonesFormControlsPage.validateGoodDogName,
                    errorMessages:
                    {
                        goodDogName: 'Guess again'
                    },
                    help: `For this field you have to guess the name of a good dog.
                    There are many possible good names.
                    Charlie Brown had a good dog.`
                },
                // ----------------------------------------------------
                // Textarea
                // ----------------------------------------------------
                {
                    name: 'p1',
                    title: 'Paragraph',
                    type: 'textarea',
                    validator: Validators.maxLength(250),
                    help: 'What are your life goals?'
                },
                {
                    name: 'p2',
                    title: 'Taller Paragraph',
                    textarea: 8,
                    validator: Validators.maxLength(250),
                    help: 'What are your death goals?'
                },
                {
                    name: 'p3',
                    title: 'Short Paragraph',
                    textarea:
                    {
                        rows: 2
                    },
                    validator: Validators.maxLength(250),
                    help: 'What are your rebirth goals?'
                },
                // ----------------------------------------------------
                // Numeric
                // ----------------------------------------------------
                {
                    name: 'n1',
                    title: 'Favorite Number',
                    type: 'number',
                    help: 'How about 13?'
                },
                // ----------------------------------------------------
                // Dates
                // ----------------------------------------------------
                {
                    name: 'd1',
                    title: 'd1: Date, defaults',
                    type: 'date',
                    help: 'Dinner and a movie?'
                },
                {
                    name: 'd2',
                    title: 'd2: Date, initial value, format mediumDate',
                    date: 'mediumDate',
                    initialValue: '1981-09-12',
                    help: 'Date with initial value: 1981-09-12.'
                },
                {
                    name: 'd3',
                    title: 'd3: Date, initial value, format MM/dd/yyyy',
                    date:
                    {
                        format: 'MM/dd/yyyy'
                    },
                    initialValue: 'Fri, 08 Nov 2019 00:00:00 -0500',
                    help: 'Date with initial value: Fri, 08 Nov 2019 00:00:00 -0500'
                },
                {
                    name: 'd4',
                    title: 'd4: Date, initial value, format dd-MMM-yyyy',
                    date: 'dd-MMM-yyyy',
                    initialValue: '1991-09-14',
                    help: 'Date with initial value: 1991-09-14.'
                },
                // ----------------------------------------------------
                {
                    name: 'dt1',
                    title: 'Datetime, initial value, format EEE, dd MMM yyyy HH:mm:ss Z',
                    date: 'EEE, dd MMM yyyy HH:mm:ss Z',
                    initialValue: 'Fri, 08 Nov 2019 16:20:00 -0500',
                    validator: Validators.required,
                    help: 'Having a format with time placeholders will trigger time entry.'
                },
                {
                    name: 'dt2',
                    title: 'Datetime, delayed initial value',
                    date: 'MM/dd/yyyy HH:mm:ss Z',
                    validator: Validators.required,
                    help: 'Having a format with time placeholders will trigger time entry.'
                },
                // ----------------------------------------------------
                {
                    name: 'e1',
                    title: 'e1: Elapsed time (default settings)',
                    type: 'elapsedTime'
                },
                {
                    name: 'e2',
                    title: 'e2: Elapsed time, with initial value, readonly',
                    initialValue: 90,
                    readonly: true,
                    elapsedTime:
                    {
                        returnAs: 'seconds'
                    }
                },
                // ----------------------------------------------------
                // File
                // ----------------------------------------------------
                {
                    name: 'f1',
                    hideTitle: true,
                    type: 'file',
                    help: 'I am running out of ideas for testing help text for each field type.'
                },
                // ----------------------------------------------------
                // Pickers
                // ----------------------------------------------------
                {
                    name: 'picker1',
                    title: '1 Read-only',
                    picker: [ 'This', 'That', 'The other' ],
                    help: 'One o\'clock rock',
                    onChange: (v, i) => console.log('picker1 change', v, i)
                },
                {
                    name: 'picker2',
                    title: '2 Read-write',
                    picker:
                    {
                        readWrite: true,
                        values: [ 'More', 'Options', 'Shaking', 'Stick' ]
                    },
                    help: 'Pickers are cool.'
                },
                {
                    name: 'picker3',
                    title: '3 Map of numbers, initialValue',
                    picker: picker3Map,
                    initialValue: 2
                },
                {
                    name: 'picker4',
                    title: '4 Map of strings, initialValue',
                    picker: picker4Map,
                    initialValue: 'B'
                },
                {
                    name: 'picker5',
                    title: '5 Populator, map, cache',
                    picker: () => this.getDeferredPickerValues1()
                },
                {
                    name: 'picker5a',
                    title: '5a Populator, map, nocache',
                    picker:
                    {
                        populator: () => this.getDeferredPickerValues1(),
                        nocache: true
                    }
                },
                {
                    name: 'picker5b',
                    title: '5b Populator, error, nocache',
                    initialValue: 'bob',
                    picker:
                    {
                        populator: () => this.badPopulator(),
                        nocache: true
                    }
                },
                {
                    name: 'picker6',
                    title: '6 Populator, map, initialValue',
                    picker: () => this.getDeferredPickerValues2(),
                    initialValue: 'B'
                },
                {
                    name: 'picker6a',
                    title: '6a Populator, map, bad initialValue',
                    picker: () => this.getDeferredPickerValues2(),
                    initialValue: 'D'
                },
                {
                    name: 'picker7',
                    title: '7 Populator, map, nocache, iv, multi',
                    initialValue: [ 'A', 'C' ],
                    picker:
                    {
                        populator: () => this.getDeferredPickerValues1(),
                        nocache: true,
                        multi: true
                    }
                },
                {
                    name: 'picker7a',
                    title: '7a Array, multi',
                    picker:
                    {
                        values: [ 'yacco', 'wacko', 'dot' ],
                        multi: true
                    }
                },
                // ----------------------------------------------------
                // Toggles
                // ----------------------------------------------------
                {
                    name: 'toggle1',
                    title: 'Toggle',
                    toggle: { on: 'YES', off: 'Maybe' },
                    initialValue: 'YES',
                    help: 'It is what it is unless it isn\'t.'
                },
                {
                    name: 'toggle2',
                    title: 'Numeric toggle',
                    toggle: { on: 1, off: 0 },
                    help: 'Roll another one'
                },
                {
                    name: 'toggle3',
                    title: 'Default boolean toggle',
                    type: 'toggle'
                }
            ]
        });

        // Test setting value after form is created
        this.bf.setValue('dt2', 'Fri, 12 Dec 2019 12:12:00 -0500');

        this.display = this.bf.getValues();
    }

    /**
     * Custom form validator to validate good dog names.
     * 
     * @param control Form control
     * @returns null for no errors, or ValidationErrors object populated with failed validator names.
     */
    private static validateGoodDogName(control: UntypedFormControl) : ValidationErrors | null
    {
        switch (control.value)
        {
            case undefined:
            case '':
            case 'Snoopy':
            case 'Woodstock':
                return null;
            default:
                return { goodDogName: true };
        }
    }

    submit()
    {
        this.display = this.bf.getValues();
    }

    debug()
    {
        console.log(this.bf, this.bf.getValues());
        this.display = this.bf.getValues();
    }

    hide()
    {
        const last = this.bf.items.length - 1;
        this.bf.items[last].hidden = !this.bf.items[last].hidden;
    }

    pickerValues()
    {
        this.bf.getItem('picker1').picker.setValues([ 'Apple', 'Banana', 'Commadore' ]);
    }

    async getDeferredPickerValues1() : Promise<BonesFormItemPickerValues>
    {
        const map = new Map<string, string>();

        map.set('A', 'Apple');
        map.set('B', 'Banana');
        map.set('C', 'Cherry');

        console.log('getDeferredPickerValues1', map);
        return map;
    }
    async getDeferredPickerValues2() : Promise<BonesFormItemPickerValues>
    {
        const map = new Map<string, string>();

        map.set('A', 'Apple');
        map.set('B', 'Banana');
        map.set('C', 'Cherry');

        console.log('getDeferredPickerValues2', map);
        return map;
    }

    async badPopulator() : Promise<BonesFormItemPickerValues>
    {
        const error = new BonesError('badPopulator always throws an error');
        this.bes.errorHandler(error);
        throw error;
    }
}
